import React, { useEffect, useMemo, useState } from 'react';
import PageHero from '../components/PageHero';
import CtaBand from '../components/CtaBand';
import BookingForm, { BookingPreselect } from '../components/booking/BookingForm';
import DateProposalModal from '../components/booking/DateProposalModal';
import TourShowcaseCalendar from '../components/booking/TourShowcaseCalendar';
import { publicApi } from '../api/insolita';
import { CalendarDay, SlotAvailability, Tour as TourType, TourCalendarEntry } from '../api/types';
import { useContent } from '../content/ContentContext';
import { useUi } from '../i18n/ui';
import { resolveTour } from '../i18n/tour';
import { renderMultiline } from '../utils/text';

const FALLBACK_IMG = ['/img/allenamento-motorally.jpg', '/img/extreme-insolita.jpg', '/img/allenamento-2022-insolita-sardegna.jpg'];

const TourPage: React.FC = () => {
  const { t, img } = useContent();
  const { tr, lang } = useUi();
  const [tours, setTours] = useState<TourType[]>([]);
  const [slots, setSlots] = useState<SlotAvailability[]>([]);
  const [calendar, setCalendar] = useState<CalendarDay[]>([]);
  const [calLoading, setCalLoading] = useState(true);
  const [preselect, setPreselect] = useState<BookingPreselect>();
  // Modal per "Proponi tu le date" (booking con isProposal=true).
  const [proposalOpen, setProposalOpen] = useState(false);
  const [proposalTourId, setProposalTourId] = useState<number | undefined>(undefined);

  useEffect(() => {
    publicApi.tours().then(setTours).catch(() => setTours([]));
    publicApi.upcomingSlots().then(setSlots).catch(() => setSlots([]));
    const pad = (n: number) => String(n).padStart(2, '0');
    const isoOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const from = new Date();
    const to = new Date(); to.setMonth(to.getMonth() + 6);
    publicApi.publicCalendar(isoOf(from), isoOf(to))
      .then(setCalendar)
      .catch(() => setCalendar([]))
      .finally(() => setCalLoading(false));
  }, []);

  const pickFromCalendar = (entry: TourCalendarEntry, date: string) => {
    setPreselect({
      tourId: entry.tourId,
      slotId: entry.slotId ?? undefined,
      checkIn: entry.bookingMode === 'FLEXIBLE' ? date : undefined,
      nonce: (preselect?.nonce ?? 0) + 1,
    });
    setTimeout(() => document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  // Riepilogo prezzi dinamico ("a partire da" = tour più economico tra quelli attivi).
  const priceInfo = useMemo(() => {
    const src = tours.filter((t) => t.active !== false);
    const list = src.length ? src : tours;
    if (!list.length) return { price: 75, minPeople: 3, deposit: 25 };
    const prices = list.map((t) => t.pricePerPersonPerDay).filter((n) => typeof n === 'number' && n > 0);
    const price = prices.length ? Math.min(...prices) : 75;
    const minPeople = Math.min(...list.map((t) => t.minPeople || 1));
    const deposit = list[0].depositPercent ?? 25;
    return { price, minPeople, deposit };
  }, [tours]);

  // Riattiva il reveal-on-scroll per gli article dei tour caricati async.
  // usePageEffects gira al mount del layout, quando i tour non sono ancora nel DOM.
  useEffect(() => {
    if (tours.length === 0) return;
    const t = setTimeout(() => {
      const revs = Array.from(document.querySelectorAll<HTMLElement>(".reveal:not(.in)"));
      if (!revs.length) return;
      if ("IntersectionObserver" in window) {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach((e) => {
            if (e.isIntersecting) { e.target.classList.add("in"); obs.unobserve(e.target); }
          });
        }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
        revs.forEach((el) => io.observe(el));
      } else {
        revs.forEach((el) => el.classList.add("in"));
      }
    }, 30);
    return () => clearTimeout(t);
  }, [tours.length]);

  useEffect(() => {
    if (window.location.hash === '#prenota') {
      setTimeout(() => document.getElementById('prenota')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [tours]);

  const phone = t('contact.phone', '+393467814537');

  return (
    <>
      <PageHero image={img('tour.hero.image', '/img/insolita-sardegna-moto.jpg')} crumb={tr('page.tour.title')} title={tr('page.tour.title')} />

      <section className="sec">
        <div className="wrap">
          <div className="sec-head reveal">
            <div>
              <span className="eyebrow">{tr('tp.eyebrow')}</span>
              <h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(tr('tp.title'))}</h2>
            </div>
            <p className="lead" style={{ maxWidth: '38ch' }}>{tr('tp.lead')}</p>
          </div>

          {tours.map((tour, i) => {
            const r = resolveTour(tour, lang);
            return (
            <article key={tour.id} className={`pkg reveal${i % 2 === 1 ? ' pkg--rev' : ''}`}>
              <div className="pkg__media">
                <img src={tour.imageUrl || FALLBACK_IMG[i % FALLBACK_IMG.length]} alt={r.title} />
              </div>
              <div className="pkg__body">
                <h3>{r.title}</h3>
                {r.subtitle && <span className="dur">{r.subtitle}</span>}
                <p>{r.description}</p>
                <div className="incl">
                  {r.included?.length > 0 && (
                    <div className="g-yes">
                      <h4>{tr('tp.included')}</h4>
                      <ul className="yes">{r.included.map((x, k) => <li key={k}>{x}</li>)}</ul>
                    </div>
                  )}
                  {r.notIncluded?.length > 0 && (
                    <div className="g-no">
                      <h4>{tr('tp.notIncluded')}</h4>
                      <ul className="no">{r.notIncluded.map((x, k) => <li key={k}>{x}</li>)}</ul>
                    </div>
                  )}
                </div>
                <a href="#prenota" className="btn btn-acid">{tr('tp.book')}</a>
              </div>
            </article>
            );
          })}

          {tours.length === 0 && (
            <p className="lead">{tr('tp.none').split('{phone}')[0]}<a href={`tel:${phone}`}>{phone}</a>{tr('tp.none').split('{phone}')[1] || ''}</p>
          )}
        </div>
      </section>

      {/* CALENDARIO DISPONIBILITÀ (tutti i pacchetti) */}
      {(calLoading || calendar.length > 0) && (
        <section className="sec sec--ink">
          <div className="wrap">
            <div className="sec-head reveal">
              <div>
                <span className="eyebrow on-dark">{tr('cal.showcase.eyebrow')}</span>
                <h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(tr('cal.showcase.title'))}</h2>
              </div>
              <p className="lead" style={{ maxWidth: '34ch' }}>{tr('cal.showcase.lead')}</p>
            </div>
            <div className="reveal">
              <TourShowcaseCalendar days={calendar} tours={tours} onPick={pickFromCalendar} loading={calLoading} />
            </div>
          </div>
        </section>
      )}

      {/* PRENOTA */}
      <section className="sec sec--ink" id="prenota">
        <div className="wrap">
          <div className="sec-head reveal">
            <div>
              <span className="eyebrow on-dark">{tr('tp.prenota.eyebrow')}</span>
              <h2 className="h-sec" style={{ marginTop: '.35em' }}>{renderMultiline(tr('tp.prenota.title'))}</h2>
              <p className="lead" style={{ maxWidth: '48ch', marginTop: 12 }}>{tr('tp.prenota.lead')}</p>
            </div>
            {/* MOD-PROPOSTA-DATE-HERO — box "Proponi le tue date" spostato in header
                per essere piu visibile (era in fondo alla sezione). */}
            <div style={{ padding: '20px 24px', border: '1px dashed rgba(228,255,26,.3)', borderRadius: 6, background: 'rgba(228,255,26,.05)', flex: '0 1 480px', minWidth: 320 }}>
              <p style={{ margin: '0 0 4px', fontSize: 14, color: '#E4FF1A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>Non trovi le tue date?</p>
              <p style={{ margin: '0 0 12px', fontSize: 14, color: 'rgba(244,241,233,.75)' }}>Proponi tu il periodo che preferisci: valuteremo la disponibilità e ti risponderemo entro pochi giorni.</p>
              <button type="button" onClick={() => { setProposalTourId(preselect?.tourId ?? tours[0]?.id); setProposalOpen(true); }}
                      style={{ background: 'transparent', color: '#E4FF1A', border: '1px solid #E4FF1A', padding: '10px 22px', borderRadius: 3, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '.05em', cursor: 'pointer' }}>
                Proponi le tue date
              </button>
            </div>
          </div>
          <div className="book reveal">
            <div>
              <div className="book-meta">
                <div className="m">{tr('tp.meta.orario')}</div>
                <div className="m">{tr('tp.meta.min').replace('{n}', String(priceInfo.minPeople))}</div>
                <div className="m">{tr('tp.meta.email')}</div>
              </div>
              <div className="book-price"><b>{priceInfo.price}€</b><span className="from">{renderMultiline(tr('tp.price.from'))}</span></div>
              <p className="book-note">{tr('tp.price.note').replace('{deposit}', String(priceInfo.deposit))}</p>
            </div>
            <BookingForm tours={tours} slots={slots} preselect={preselect} />
          </div>
        </div>
      </section>

      {proposalOpen && (
        <DateProposalModal tours={tours} defaultTourId={proposalTourId} onClose={() => setProposalOpen(false)} />
      )}

      <CtaBand
        image={img('tour.cta.image', '/img/insolita-sardegna-experience.jpg')}
        eyebrow={tr('tp.cta.eyebrow')}
        title={tr('tp.cta.title')}
        text={tr('tp.cta.text')}
      >
        <div className="band__phone"><span>{tr('tp.cta.phone')}</span><a href={`tel:${phone}`}>(+39) {phone.replace('+39', '')}</a></div>
      </CtaBand>
    </>
  );
};

export default TourPage;
